// Phrases the generic brand substitution must not touch: legal-entity/document
// names (the Acre Support entities keep their name — title-case "Support"
// only), and land-measurement usages of "acre". A phrase with an entry in
// PHRASE_REWRITES is replaced by that exact text (which is itself exempt from
// the generic rules); the rest are carried through verbatim.
const PROTECTED_PHRASES = [
  'Acre Platforms Limited',
  'Acre Platforms Ltd',
  'Acre Acceptable Use Policy',
  'Acre Software Licence Agreement',
  'Acre Support Ltd',
  'Acre Support UK',
  'Acre Support',
  'one acre (0.404ha)',
  'one acre',
].sort((a, b) => b.length - a.length)

// Legal entity names are never contracted: always "Limited", never "Ltd" —
// the contracted form also normalises to the full legal name so it can't
// degrade into "Score. Platforms Ltd" via the generic substitution.
const PHRASE_REWRITES: Record<string, string> = {
  'acre platforms limited': 'Acre Platforms Limited trading as Score.',
  'acre platforms ltd': 'Acre Platforms Limited trading as Score.',
}

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

const PROTECTED_PATTERN = new RegExp(PROTECTED_PHRASES.map(escapeRegExp).join('|'), 'gi')

// The support entity is only the title-case "Support" form; lowercase "support"
// refers to the company's support function, so the mention rebrands as usual
// ("contact Acre support" → "contact Score. support").
const SUPPORT_ENTITY_PREFIX = /^acre support/i

const POSSESSIVE_PATTERN = /\b[Aa]cre's\b/g
const WHOLE_WORD_PATTERN = /\b[Aa]cre\b/g
const DOUBLE_DOT_PATTERN = /Score\.\.(?!\.)/g

const brandSegment = (segment: string): string =>
  segment.replace(POSSESSIVE_PATTERN, "Score's").replace(WHOLE_WORD_PATTERN, 'Score.')

const resolveProtectedMatch = (matched: string): string => {
  const rewrite = PHRASE_REWRITES[matched.toLowerCase()]
  if (rewrite) {
    return rewrite
  }
  if (SUPPORT_ENTITY_PREFIX.test(matched) && !matched.includes('Support')) {
    return brandSegment(matched)
  }
  return matched
}

// Splits on the protected phrases so they're carried through verbatim (or
// swapped for their rewrite), and only runs the brand substitution over the
// text in between. The double-dot collapse runs on the joined result so a
// sentence-ending "Score.." straddling a segment boundary still collapses.
// For strings that don't come through the i18n message map — e.g. quote notes
// returned by the conveyancing API and rendered verbatim.
export const applyScoreBrandingToText = (value: string): string => {
  const segments: string[] = []
  let lastIndex = 0

  PROTECTED_PATTERN.lastIndex = 0
  let match = PROTECTED_PATTERN.exec(value)

  while (match !== null) {
    segments.push(brandSegment(value.slice(lastIndex, match.index)))
    segments.push(resolveProtectedMatch(match[0]))
    lastIndex = match.index + match[0].length
    match = PROTECTED_PATTERN.exec(value)
  }

  segments.push(brandSegment(value.slice(lastIndex)))

  return segments.join('').replace(DOUBLE_DOT_PATTERN, 'Score.')
}

export const applyScoreBranding = (messages: Record<string, string>): Record<string, string> => {
  const result: Record<string, string> = {}

  Object.entries(messages).forEach(([key, value]) => {
    result[key] = typeof value === 'string' ? applyScoreBrandingToText(value) : value
  })

  return result
}
